import { useEffect, useRef, useState } from 'react';
import { audioContext } from '@/audio/context';
import { recordedSheets } from '@/audio/recorded';
import { SheetVoice, WebVoice, type SheetLibrary } from '@/audio/voice';
import { navigate } from '@/app/router';
import { floors } from '@/content/floors';
import { STORY, STORY_PAUSE_MS, stripTags, type StoryScene } from '@/content/story';
import { strings } from '@/content/strings';
import { SettingsDialog } from '@/features/settings/SettingsDialog';
import { agentStore } from '@/game/agentProfile';
import { markIntroSeen } from '@/game/intro';
import { ageBand, useSettings } from '@/game/settings';
import { useOrientation } from '@/lib/orientation';
import { SceneStage } from './SceneStage';
import { sceneImages } from './sceneLayers';
import { cutDelay, isReveal } from './timeline';
import styles from './Intro.module.css';

/** The diagonal-bar wipe when the foe appears on the screen; the scene swaps under the bar. */
const WIPE_MS = 700;

const firstFloor = floors[0];

const sleep = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });

type Phase = 'ready' | 'playing' | 'paused' | 'done';

/**
 * The intro before the first mission ever (docs/design/story.md): Agent Alex tells the backstory
 * and how the game works in HQ (a layered scene, SceneStage), and at "Look!" a diagonal bar sweeps
 * the first foe onto the big screen, timed by the recording's word times; Alex's expression follows
 * the story. A tap starts it (browsers only play sound after one). It cannot be skipped (players are
 * used to skipping onboarding, and this one teaches the game); it ends on the first floor.
 */
export function Intro() {
  const { age } = useSettings();
  const [settingsOpen, setSettingsOpen] = useState(age === null);
  const orientation = useOrientation();
  const story = STORY[ageBand(age)];
  const opening = story[0]?.scene ?? 'welcome';
  const [phase, setPhase] = useState<Phase>('ready');
  const [line, setLine] = useState(-1);
  const [scene, setScene] = useState<StoryScene>(opening);
  /** The wipe on screen (a new number per reveal), or 0 when none. */
  const [wipe, setWipe] = useState(0);
  const wipes = useRef(0);
  const voice = useRef<SheetVoice | null>(null);
  const current = useRef<StoryScene>(opening);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);
  const run = useRef(0);
  const phaseRef = useRef<Phase>('ready');
  useEffect(() => {
    phaseRef.current = phase;
  }, [phase]);
  /** The line playing when the app went to the background, to continue from. */
  const resumeAt = useRef(0);

  // Every layer is fetched up front, so no change shows an empty frame.
  useEffect(() => {
    for (const url of sceneImages(orientation)) {
      const preload = new Image();
      preload.src = url;
      void preload.decode().catch(() => undefined);
    }
  }, [orientation]);

  const later = (ms: number, fn: () => void) => {
    timers.current.push(setTimeout(fn, ms));
  };
  const change = (next: StoryScene) => {
    current.current = next;
    setScene(next);
  };
  const show = (next: StoryScene) => {
    const from = current.current;
    if (from === next) return;
    if (isReveal(from, next)) {
      const id = ++wipes.current;
      setWipe(id);
      later(WIPE_MS / 2, () => {
        change(next);
      });
      later(WIPE_MS, () => {
        setWipe((w) => (w === id ? 0 : w));
      });
    } else change(next);
  };
  const halt = () => {
    run.current++;
    setWipe(0);
    voice.current?.stop();
    for (const t of timers.current) clearTimeout(t);
    timers.current = [];
  };
  useEffect(() => halt, []);

  // Leaving the app (another app, the lock screen) pauses the briefing: iOS suspends the audio, so
  // the line would never end and the briefing froze (QA 2026-10-12). Coming back offers to continue
  // at the line that was interrupted.
  useEffect(() => {
    const onHidden = () => {
      if (document.visibilityState !== 'hidden' || phaseRef.current !== 'playing') return;
      halt();
      setPhase('paused');
    };
    document.addEventListener('visibilitychange', onHidden);
    window.addEventListener('pagehide', onHidden);
    return () => {
      document.removeEventListener('visibilitychange', onHidden);
      window.removeEventListener('pagehide', onHidden);
    };
  }, []);

  const tell = async (from = 0) => {
    halt();
    const id = run.current;
    setPhase('playing');
    if (from === 0) {
      current.current = opening;
      setScene(opening);
    }
    const library: SheetLibrary | undefined = await recordedSheets();
    voice.current ??= new SheetVoice(
      audioContext,
      new WebVoice(audioContext),
      Promise.resolve(library),
    );
    // The library was handed over as a resolved promise; let the voice pick it up.
    await sleep(0);
    for (const [i, segment] of story.entries()) {
      if (i < from) continue;
      if (id !== run.current) return;
      resumeAt.current = i;
      setLine(i);
      if (!segment.cutAt) show(segment.scene);
      else if (segment.lead) show(segment.lead);
      const cut = { scheduled: false };
      const outcome = await voice.current.play([{ kind: 'say', text: segment.text }], {
        onPart: (_, seconds) => {
          if (!segment.cutAt || cut.scheduled) return;
          cut.scheduled = true;
          const words = library?.index.words(segment.text);
          later(cutDelay(segment, words, seconds) * 1000, () => {
            show(segment.scene);
          });
        },
      });
      if (id !== run.current || outcome === 'interrupted') return;
      if (segment.cutAt && !cut.scheduled) show(segment.scene);
      await sleep(STORY_PAUSE_MS);
    }
    if (id === run.current) setPhase('done');
  };

  const begin = () => {
    // Resume audio inside the tap: iOS only starts sound from a user gesture.
    void audioContext().resume();
    void tell();
  };
  const resume = () => {
    void audioContext().resume();
    void tell(resumeAt.current);
  };
  const go = () => {
    halt();
    markIntroSeen();
    // The first time, the child makes their agent before the first mission (agent-builder.md).
    if (!agentStore.get().made) navigate({ page: 'agent' });
    else if (firstFloor) navigate({ page: 'floor', floorId: firstFloor.id });
    else navigate({ page: 'elevator' });
  };

  const t = strings.intro;
  const caption = line >= 0 ? stripTags(story[line]?.text ?? '') : '';
  return (
    <div className={styles.root}>
      <SceneStage orientation={orientation} scene={scene} wipe={wipe} />
      <div className={styles.ui}>
        <h1 className={styles.title}>{t.title}</h1>
        <div className={styles.bottom}>
          <p className={styles.caption} aria-live="polite">
            {caption}
          </p>
          <div className={styles.actions}>
            {phase === 'ready' && (
              <button type="button" className={styles.primary} onClick={begin}>
                {t.begin}
              </button>
            )}
            {phase === 'paused' && (
              <button type="button" className={styles.primary} onClick={resume}>
                {t.resume}
              </button>
            )}
            {phase === 'done' && (
              <>
                <button type="button" className={styles.primary} onClick={go}>
                  {t.go}
                </button>
                <button type="button" className={styles.secondary} onClick={begin}>
                  {t.replay}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
      {settingsOpen && (
        <SettingsDialog
          onClose={() => {
            setSettingsOpen(false);
          }}
        />
      )}
    </div>
  );
}
